import React from 'react'
import { useApp } from '../context/AppContext'
import { Icon, useT, useIsMobile } from '../shared'
import { useOrgName } from "../shared/BrandLogo"
import { Card, Btn, Badge } from '../shared/components'
import { getTrainingHubOverview, getTrainingHubParticipant } from '../api/trainingHub.api'
import { rateSession, joinZoom } from '../api/sessions.api'
import { bukaTautan } from '../lib/bukaTautan'
import { postTranscript, stopTranslation, createSubtitleStream } from '../api/subtitles.api'
import { tintBg, tintFg } from '../lib/tint'

/* ============================================================
   FR-32 — Training Hub — OSCT-2 design
   Admin/trainer: metrics + quick actions + Overview/Modules/
   Activity tabs. Participant: live banner + sessions + rating.
   Zoom v1 = join link only (no in-app stream).
   ============================================================ */

const ST = {
  live: { color: tintFg('#1D9E75'), bg: tintBg('#1D9E75') },
  scheduled: { color: tintFg('#378ADD'), bg: tintBg('#378ADD') },
  completed: { color: 'var(--text-3)', bg: 'var(--surface-2)' },
}
const CONF_COLOR = { connected: tintFg('#1D9E75'), ready: tintFg('#378ADD'), ended: 'var(--text-4)', missing: 'var(--danger)' }

function statusLabel(s, t) {
  return { live: t.stLive, scheduled: t.stScheduled, completed: t.stCompleted }[s] || s
}
function confLabel(s, t) {
  return { connected: t.confConnectedShort, ready: t.confReadyShort, ended: t.confEndedShort, missing: t.confMissing }[s] || s
}
function fmtDur(sec) {
  if (!sec) return '—'
  const h = Math.floor(sec / 3600); const m = Math.round((sec % 3600) / 60)
  return h ? `${h}h ${m}m` : `${m}m`
}
function fmtDate(d, lang) {
  try { return new Date(d).toLocaleString(lang === 'id' ? 'id-ID' : 'en-US', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) }
  catch { return d }
}

// Count-up for numeric metrics.
function useCountUp(target, go) {
  const [v, setV] = React.useState(0)
  React.useEffect(() => {
    if (!go || typeof target !== 'number') return
    let raf, start = null
    const step = (ts) => {
      if (!start) start = ts
      const p = Math.min((ts - start) / 800, 1)
      setV(Math.floor(p * target))
      if (p < 1) raf = requestAnimationFrame(step)
    }
    raf = requestAnimationFrame(step)
    return () => cancelAnimationFrame(raf)
  }, [target, go])
  return typeof target === 'number' ? v : target
}

// ── Live translation: trainer mic capture (Chrome SpeechRecognition) ──
const SR = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition)
const speechSupported = !!SR

// Captures the trainer's mic, emits final transcripts. Auto-restarts on
// silence-triggered onend (Chrome stops after a pause), and surfaces mic
// permission errors via onError.
function useSpeechRecognition({ sourceLang, onFinalTranscript, onError }) {
  const recRef = React.useRef(null)
  const wantRef = React.useRef(false) // should we be listening right now?
  const [listening, setListening] = React.useState(false)
  const cbRef = React.useRef({ onFinalTranscript, onError })
  cbRef.current = { onFinalTranscript, onError }

  const stop = React.useCallback(() => {
    wantRef.current = false
    setListening(false)
    try { recRef.current?.stop() } catch (_) {}
    recRef.current = null
  }, [])

  const start = React.useCallback(() => {
    if (!SR) { cbRef.current.onError?.('unsupported'); return }
    const rec = new SR()
    rec.continuous = true
    rec.interimResults = true
    rec.lang = sourceLang === 'id' ? 'id-ID' : 'en-US'
    rec.onresult = (e) => {
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i]
        if (r.isFinal) {
          const txt = r[0]?.transcript?.trim()
          if (txt) cbRef.current.onFinalTranscript?.(txt)
        }
      }
    }
    rec.onerror = (e) => {
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') {
        cbRef.current.onError?.('mic-denied')
        wantRef.current = false
        setListening(false)
      } else if (e.error === 'no-speech') {
        // benign — onend will auto-restart
      } else {
        cbRef.current.onError?.(e.error)
      }
    }
    rec.onend = () => {
      // Chrome ends the session on long silence; restart if still wanted.
      if (wantRef.current) {
        try { rec.start() } catch (_) {}
      } else {
        setListening(false)
      }
    }
    recRef.current = rec
    wantRef.current = true
    try { rec.start(); setListening(true) }
    catch (_) { /* already started */ }
  }, [sourceLang])

  React.useEffect(() => () => stop(), [stop])

  return { start, stop, listening, supported: speechSupported }
}

export default function TrainingHubScreen() {
  const { role } = useApp()
  if (role === 'participant') return <ParticipantHub />
  return <StaffHub />
}

// ── Metric card ──────────────────────────────────────────────
function Metric({ label, value, numeric }) {
  const shown = useCountUp(numeric ? value : 0, true)
  return (
    <div style={{ padding: '16px 18px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, boxShadow: 'var(--shadow-xs)' }}>
      <div style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, letterSpacing: '-0.5px', marginTop: 6, lineHeight: 1 }}>{numeric ? shown : value}</div>
    </div>
  )
}

// ── Admin / Trainer ──────────────────────────────────────────
// view: 'all' (standalone) | 'live' | 'analytics' (tabs in unified module).
// When embedded the unified Training module owns the page header.
export function StaffHub({ view = 'all', embedded = false }) {
  const t = useT()
  const { lang, currentProgram, currentProgramId, currentClass, currentClassId, navigate } = useApp()
  const orgName = useOrgName()
  const isMobile = useIsMobile()
  const [data, setData] = React.useState(null)
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState(null)

  React.useEffect(() => {
    if (!currentProgramId) return
    setLoading(true); setError(null)
    getTrainingHubOverview(currentProgramId, currentClassId)
      .then(setData)
      .catch((e) => setError(e?.response?.data?.error || e.message))
      .finally(() => setLoading(false))
  }, [currentProgramId, currentClassId])

  const m = data?.metrics
  const liveCount = data?.trainings.filter((x) => x.status === 'live').length || 0
  const totalLessons = data?.modulesRing.reduce((s, x) => s + x.lessonCount, 0) || 0

  const showLive = view === 'all' || view === 'live'
  const showAnalytics = view === 'all' || view === 'analytics'

  return (
    <div className={embedded ? '' : 'main-inner'}>
      {!embedded && (
        <div className="page-header">
          <div className="page-header-text">
            <div className="eyebrow">{(currentProgram?.code || orgName).toUpperCase()} · {t.thTitle}</div>
            <h1 className="h1" style={{ marginTop: 6 }}>{t.thTitle}</h1>
            <div className="muted mt-1">{t.thDescStaff}</div>
          </div>
          <div className="page-header-actions">
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-3)', padding: '6px 12px', background: 'var(--success-soft)', borderRadius: 999 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--success)', display: 'inline-block', animation: 'pulse-ring 1.5s ease infinite' }} />
              {t.thAllOperational}
            </div>
          </div>
        </div>
      )}

      {error && <div className="banner red mt-1" style={{ marginBottom: 12 }}><Icon name="alert" />{error}</div>}
      {loading && <div className="muted mt-5">{t.loadingText}</div>}

      {/* Analytics — metrics + modules + activity */}
      {showAnalytics && m && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: 12 }}>
            <Metric label={t.thActiveTrainings} value={m.activeTrainings} numeric />
            <Metric label={t.thTotalParticipants} value={m.totalParticipants} numeric />
            <Metric label={t.thCompletionRate} value={`${m.completionRate}%`} />
            <Metric label={t.thAvgRating} value={m.avgRating != null ? `${m.avgRating.toFixed(1)}` : '—'} />
          </div>

          <div style={{ marginTop: 20 }}>
            <div className="row between" style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 500 }}>{data.modulesRing.length} {t.thModulesTab.toLowerCase()} · {totalLessons} {t.thTotalLessons}</div>
            </div>
            {data.modulesRing.length === 0 ? (
              <div className="empty" style={{ padding: 40 }}><div className="muted">{t.thNoModules}</div></div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
                {data.modulesRing.map((mm) => <ModuleCard key={mm.id} mm={mm} t={t} />)}
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 260px', gap: 16, marginTop: 16 }}>
            <Card title={t.thActivity7d} eyebrow={t.thActivitySub}>
              <ActivityChart points={data.sparkline} />
            </Card>
            <div className="col gap-2">
              {data.sparkline.map((d) => {
                const max = Math.max(...data.sparkline.map((x) => x.value), 1)
                return (
                  <div key={d.day} className="row gap-2" style={{ alignItems: 'center' }}>
                    <div style={{ width: 44, fontSize: 11, color: 'var(--text-3)', fontWeight: 500 }}>{d.day.slice(5)}</div>
                    <div style={{ flex: 1, height: 8, background: 'var(--surface-3)', borderRadius: 99, overflow: 'hidden' }}>
                      <div style={{ width: `${(d.value / max) * 100}%`, height: '100%', background: 'var(--program-color)', opacity: 0.75, borderRadius: 99 }} />
                    </div>
                    <span className="mono" style={{ fontSize: 12, minWidth: 24, textAlign: 'right' }}>{d.value}</span>
                  </div>
                )
              })}
            </div>
          </div>
        </>
      )}

      {/* Live — operational session control */}
      {showLive && data && (
        <div className="col gap-3" style={{ marginTop: showAnalytics ? 24 : 0 }}>
          <div className="row between">
            <div style={{ fontSize: 13, fontWeight: 500 }}>
              {data.trainings.length} {t.thTrainingsToday}
              {liveCount > 0 && <span style={{ marginLeft: 10, padding: '2px 8px', borderRadius: 999, background: ST.live.bg, color: ST.live.color, fontSize: 11, fontWeight: 600 }}>{liveCount} {t.thLiveNow}</span>}
            </div>
            {/* Tanpa ini, tidak ada tanda di layar ini sendiri bahwa daftarnya
                sudah menyempit ke satu angkatan — pemilih Kelas ada di bilah
                sisi, bukan di sini. */}
            {currentClass && (
              <span className="tiny muted">{lang === 'id' ? 'Kelas' : 'Class'}: {currentClass.name}</span>
            )}
          </div>
          {data.trainings.length === 0 ? (
            <div className="empty" style={{ padding: 40 }}><div className="muted">{t.thNoSessions}</div></div>
          ) : data.trainings.map((tr) => (
            <TrainingCard key={tr.id} tr={tr} t={t} lang={lang} onManage={() => navigate('session-detail', { sessionId: tr.id })} />
          ))}
        </div>
      )}
    </div>
  )
}

function TrainingCard({ tr, t, lang, onManage }) {
  const [hov, setHov] = React.useState(false)
  const st = ST[tr.status] || ST.scheduled
  return (
    <div onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', background: hov ? 'var(--surface-2)' : 'var(--surface)', border: '1px solid var(--border-subtle)', borderLeft: `3px solid ${st.color}`, borderRadius: 10, transition: 'background 0.15s' }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: st.color, animation: tr.status === 'live' ? 'pulse-ring 1.5s ease infinite' : 'none' }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 13 }}>{tr.title}</div>
        <div className="row gap-3 muted mt-1" style={{ fontSize: 12, flexWrap: 'wrap' }}>
          <span>{tr.trainer || '—'}{(tr.mode === 'REMOTE' || tr.mode === 'HYBRID') && <span style={{ marginLeft: 6, padding: '1px 6px', borderRadius: 999, background: tintBg('#378ADD'), color: tintFg('#378ADD'), fontSize: 10, fontWeight: 500 }}>{t.thRemote}</span>}</span>
          <span className="row gap-1"><Icon name="calendar" size={11} /> {fmtDate(tr.scheduledAt, lang)}</span>
          <span className="row gap-1"><Icon name="users" size={11} /> {tr.participants}</span>
        </div>
      </div>
      <span style={{ padding: '3px 10px', borderRadius: 999, background: st.bg, color: st.color, fontSize: 11, fontWeight: 600, flexShrink: 0 }}>{statusLabel(tr.status, t)}</span>
      {tr.conferenceStatus && (
        <span style={{ fontSize: 11, color: CONF_COLOR[tr.conferenceStatus], fontWeight: 600, flexShrink: 0, minWidth: 72 }}>
          <Icon name="video" size={12} /> {confLabel(tr.conferenceStatus, t)}
        </span>
      )}
      {tr.status === 'live' && <TranslationToggle sessionId={tr.id} t={t} />}
      <Btn size="sm" style={{ flexShrink: 0 }} onClick={onManage}>{t.thManage}</Btn>
    </div>
  )
}

// Trainer-side live translation control: pick spoken language + toggle mic.
// Self-contained per live session so multiple live cards each broadcast
// independently. Mounted only for live sessions.
function TranslationToggle({ sessionId, t }) {
  const [sourceLang, setSourceLang] = React.useState('id')
  const [err, setErr] = React.useState(null)
  const seqRef = React.useRef(0)

  const onFinalTranscript = React.useCallback((text) => {
    seqRef.current += 1
    postTranscript(sessionId, text, sourceLang, seqRef.current).catch(() => {})
  }, [sessionId, sourceLang])

  const onError = React.useCallback((code) => {
    setErr(code === 'mic-denied' ? t.subtitleMicDenied : code === 'unsupported' ? t.subtitleUnsupported : code)
  }, [t])

  const { start, stop, listening, supported } = useSpeechRecognition({ sourceLang, onFinalTranscript, onError })

  const toggle = () => {
    setErr(null)
    if (listening) {
      stop()
      stopTranslation(sessionId).catch(() => {})
    } else {
      start()
    }
  }

  if (!supported) {
    return (
      <span title={t.subtitleUnsupported} style={{ fontSize: 11, color: 'var(--text-4)', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
        <Icon name="mic" size={12} /> —
      </span>
    )
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
      {/* Spoken-language picker (locked while broadcasting) */}
      <div style={{ display: 'flex', border: '1px solid var(--border-strong)', borderRadius: 7, overflow: 'hidden' }}>
        {['id', 'en'].map((l) => (
          <button key={l} disabled={listening} onClick={() => setSourceLang(l)}
            style={{ padding: '4px 8px', fontSize: 10, fontWeight: 600, border: 'none', cursor: listening ? 'default' : 'pointer', background: sourceLang === l ? 'var(--program-color)' : 'transparent', color: sourceLang === l ? 'white' : 'var(--text-3)', opacity: listening && sourceLang !== l ? 0.4 : 1 }}>
            {l.toUpperCase()}
          </button>
        ))}
      </div>
      <button onClick={toggle} title={err || t.subtitleToggle}
        style={{ padding: '6px 12px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5, background: listening ? '#D85A30' : 'var(--surface-3)', color: listening ? 'white' : 'var(--text-2)' }}>
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: listening ? '#fff' : 'var(--text-4)', animation: listening ? 'pulse-ring 1.2s ease infinite' : 'none' }} />
        <Icon name="mic" size={12} color={listening ? 'white' : 'currentColor'} /> {t.subtitleToggle}
      </button>
    </div>
  )
}

function ModuleCard({ mm, t }) {
  const [hov, setHov] = React.useState(false)
  return (
    <div onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ padding: 18, background: hov ? 'var(--program-color-soft)' : 'var(--surface)', border: `1px solid ${hov ? 'var(--program-color)' : 'var(--border-subtle)'}`, borderRadius: 12, cursor: 'default', transition: 'all 0.15s', transform: hov ? 'translateY(-2px)' : 'none', boxShadow: hov ? 'var(--shadow-sm)' : 'none' }}>
      <div style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--program-color-soft)', color: 'var(--program-color-deep)', display: 'grid', placeItems: 'center', marginBottom: 10 }}>
        <Icon name="layers" size={18} color="var(--program-color-text)" />
      </div>
      <div style={{ fontWeight: 600, fontSize: 13 }}>{mm.title}</div>
      <div className="row gap-2 muted mt-1" style={{ fontSize: 12 }}>
        <span>{mm.lessonCount} {t.thLessonsWord}</span><span>·</span><span>{fmtDur(mm.durationSec)}</span>
      </div>
    </div>
  )
}

function ActivityChart({ points }) {
  const W = 560, H = 200, PL = 40, PB = 28, PT = 16, PR = 16
  const cW = W - PL - PR, cH = H - PT - PB
  const vals = points.map((p) => p.value)
  const maxV = Math.max(...vals, 1) + 1
  const xs = points.map((_, i) => PL + (i / Math.max(points.length - 1, 1)) * cW)
  const ys = points.map((p) => PT + cH - (p.value / maxV) * cH)
  const line = xs.map((x, i) => `${i === 0 ? 'M' : 'L'} ${x} ${ys[i]}`).join(' ')
  const area = `${line} L ${xs[xs.length - 1]} ${PT + cH} L ${xs[0]} ${PT + cH} Z`
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: 'block', overflow: 'visible', marginTop: 8 }}>
      <defs>
        <linearGradient id="th-area" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="var(--program-color)" stopOpacity="0.18" />
          <stop offset="1" stopColor="var(--program-color)" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#th-area)" />
      <path d={line} fill="none" stroke="var(--program-color)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      {points.map((p, i) => (
        <g key={i}>
          <text x={xs[i]} y={H - 6} fontSize="10" fill="var(--text-4)" textAnchor="middle">{p.day.slice(5)}</text>
          <circle cx={xs[i]} cy={ys[i]} r="4" fill="var(--surface)" stroke="var(--program-color)" strokeWidth="2.5" />
        </g>
      ))}
    </svg>
  )
}

// Participant-side subtitle overlay: subscribes to the live SSE stream for a
// session, shows translated captions, lets the viewer toggle ID/EN, and fades
// out after a few seconds of silence. Rendered inside the live banner.
function SubtitleOverlay({ sessionId, t, lang }) {
  const [subtitle, setSubtitle] = React.useState(null) // { id, en, sourceLang }
  const [displayLang, setDisplayLang] = React.useState(lang)
  const [visible, setVisible] = React.useState(false)
  const [status, setStatus] = React.useState('connecting') // connecting | live | retrying
  const fadeRef = React.useRef(null)
  const esRef = React.useRef(null)
  const retryRef = React.useRef(null)
  const backoffRef = React.useRef(3000)

  React.useEffect(() => {
    if (!sessionId) return
    let closed = false

    const connect = async () => {
      if (closed) return
      let es
      try {
        es = await createSubtitleStream(sessionId)
      } catch (_) {
        // Tiket gagal diminta (jaringan, sesi login habis): coba lagi seperti
        // sambungan yang putus.
        if (closed) return
        setStatus('retrying')
        retryRef.current = setTimeout(connect, backoffRef.current)
        backoffRef.current = Math.min(backoffRef.current * 2, 30000)
        return
      }
      if (closed) { es.close(); return }
      esRef.current = es

      es.addEventListener('subtitle', (ev) => {
        try {
          const d = JSON.parse(ev.data)
          setSubtitle({ id: d.id, en: d.en, sourceLang: d.sourceLang })
          setVisible(true)
          clearTimeout(fadeRef.current)
          fadeRef.current = setTimeout(() => setVisible(false), 8000)
        } catch (_) {}
      })
      es.addEventListener('control', (ev) => {
        try {
          const d = JSON.parse(ev.data)
          if (d.type === 'translation-start') setStatus('live')
          if (d.type === 'translation-stop') { setVisible(false); setSubtitle(null) }
        } catch (_) {}
      })
      es.onopen = () => { setStatus('live'); backoffRef.current = 3000 }
      es.onerror = () => {
        es.close()
        if (closed) return
        setStatus('retrying')
        retryRef.current = setTimeout(connect, backoffRef.current)
        backoffRef.current = Math.min(backoffRef.current * 2, 30000)
      }
    }
    connect()

    return () => {
      closed = true
      clearTimeout(fadeRef.current)
      clearTimeout(retryRef.current)
      try { esRef.current?.close() } catch (_) {}
    }
  }, [sessionId])

  const text = subtitle ? (displayLang === 'id' ? subtitle.id : subtitle.en) : ''

  return (
    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)', padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 12, opacity: visible && text ? 1 : 0, transition: 'opacity 400ms ease', pointerEvents: visible && text ? 'auto' : 'none' }}>
      {/* Source-language badge */}
      <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.55)', flexShrink: 0, borderRight: '1px solid rgba(255,255,255,0.2)', paddingRight: 10 }}>
        <Icon name="globe" size={11} color="rgba(255,255,255,0.55)" />
        {t.subtitleFrom} {(subtitle?.sourceLang || 'id').toUpperCase()}
      </span>
      {/* Caption text */}
      <span style={{ flex: 1, fontSize: 13, fontWeight: 500, color: 'white', lineHeight: 1.4, textAlign: 'center' }}>{text}</span>
      {/* Display-language toggle */}
      <div style={{ display: 'flex', flexShrink: 0, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 6, overflow: 'hidden' }}>
        {['id', 'en'].map((l) => (
          <button key={l} onClick={() => setDisplayLang(l)}
            style={{ padding: '3px 9px', fontSize: 10, fontWeight: 700, border: 'none', cursor: 'pointer', background: displayLang === l ? 'rgba(255,255,255,0.25)' : 'transparent', color: 'white' }}>
            {l.toUpperCase()}
          </button>
        ))}
      </div>
    </div>
  )
}

// ── Participant ──────────────────────────────────────────────
export function ParticipantHub({ embedded = false }) {
  const t = useT()
  const { lang, currentProgramId } = useApp()
  const orgName = useOrgName()
  const [data, setData] = React.useState(null)
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState(null)

  const reload = React.useCallback(() => {
    if (!currentProgramId) return
    setLoading(true); setError(null)
    getTrainingHubParticipant(currentProgramId)
      .then(setData)
      .catch((e) => setError(e?.response?.data?.error || e.message))
      .finally(() => setLoading(false))
  }, [currentProgramId])
  React.useEffect(() => { reload() }, [reload])

  const liveSession = data?.upcoming.find((s) => (s.isLive || s.zoomJoinable) && s.conferenceUrl)
  const gabung = (s) => gabungSesi(s).catch((e) => setError(e?.response?.data?.error || e.message))
  // Unified session list: upcoming/live first, then attended history.
  const allSessions = data ? [...data.upcoming, ...data.history.filter((h) => !data.upcoming.some((u) => u.id === h.id))] : []

  return (
    <div className={embedded ? '' : 'main-inner'}>
      {!embedded && (
        <div className="page-header">
          <div className="page-header-text">
            <div className="eyebrow">{orgName} · {t.thMySessions}</div>
            <h1 className="h1" style={{ marginTop: 6 }}>{t.thTitle}</h1>
          </div>
        </div>
      )}

      {error && <div className="banner red mt-1" style={{ marginBottom: 12 }}><Icon name="alert" />{error}</div>}
      {loading && <div className="muted mt-5">{t.loadingText}</div>}

      {data && (
        <>
          {/* Live banner */}
          {liveSession && (
            <div style={{ padding: '16px 20px', background: 'linear-gradient(135deg, #0E4D3A, #1D9E75)', borderRadius: 14, color: 'white', display: 'flex', alignItems: 'center', gap: 20, marginBottom: 20, position: 'relative', overflow: 'hidden' }}>
              <svg viewBox="0 0 800 80" style={{ position: 'absolute', inset: 0, opacity: 0.12 }} preserveAspectRatio="none">
                <path d="M0 40 Q 200 10, 400 40 T 800 40" fill="none" stroke="white" strokeWidth="1.5" />
                <path d="M0 55 Q 200 25, 400 55 T 800 55" fill="none" stroke="white" strokeWidth="1" />
              </svg>
              <div style={{ width: 48, height: 48, borderRadius: 12, background: 'rgba(255,255,255,0.15)', display: 'grid', placeItems: 'center', flexShrink: 0, position: 'relative' }}>
                <Icon name="video" size={22} color="white" />
                <span style={{ position: 'absolute', top: -4, right: -4, width: 12, height: 12, borderRadius: '50%', background: '#FF4040', border: '2px solid #1D9E75', animation: 'pulse-ring 1.2s ease infinite' }} />
              </div>
              <div style={{ flex: 1, position: 'relative' }}>
                <div style={{ fontWeight: 600, fontSize: 15 }}>{liveSession.title} — {t.thLiveBanner}</div>
                <div style={{ fontSize: 12, opacity: 0.85, marginTop: 3 }}>{liveSession.trainer || '—'} · {fmtDate(liveSession.scheduledAt, lang)}</div>
              </div>
              <button onClick={() => gabung(liveSession)}
                style={{ padding: '10px 22px', background: 'white', color: '#0E4D3A', border: 'none', borderRadius: 10, fontWeight: 600, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7, flexShrink: 0, position: 'relative' }}>
                <Icon name="video" size={14} /> {t.thJoinStream}
              </button>
              <SubtitleOverlay sessionId={liveSession.id} t={t} lang={lang} />
            </div>
          )}

          {/* All sessions */}
          <Card title={t.thAllSessions} noPad>
            {allSessions.length === 0 ? (
              <div className="empty" style={{ padding: 40 }}><div className="muted">{t.thNoUpcoming}</div></div>
            ) : allSessions.map((s) => (
              <ParticipantSessionRow key={s.id} s={s} t={t} lang={lang} onRated={reload} onErr={setError} />
            ))}
          </Card>

          {/* Info notice */}
          <div style={{ marginTop: 16, padding: '12px 16px', background: 'var(--surface-2)', border: '1px solid var(--border-subtle)', borderRadius: 10, fontSize: 12, color: 'var(--text-3)', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <Icon name="shield" size={14} style={{ flexShrink: 0, marginTop: 1 }} />
            <div>{t.thParticipantNote}</div>
          </div>

          {data.trainers.length > 0 && (
            <Card title={t.thTrainers} className="mt-4">
              <div className="row gap-2" style={{ flexWrap: 'wrap' }}>
                {data.trainers.map((tr) => <Badge key={tr.id} tone="outline">{tr.name}</Badge>)}
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  )
}

// Meeting otomatis memakai tautan pribadi, karena tautan itulah yang membuat
// kehadiran peserta bisa dicocokkan dari laporan Zoom.
async function gabungSesi(s) {
  if (!s.zoomManaged) {
    window.open(s.conferenceUrl, '_blank', 'noopener')
    return
  }
  await bukaTautan(() => joinZoom(s.id).then((r) => r.joinUrl))
}

function ParticipantSessionRow({ s, t, lang, onRated, onErr }) {
  const [rating, setRating] = React.useState(0)
  const [comment, setComment] = React.useState('')
  const [busy, setBusy] = React.useState(false)
  const [open, setOpen] = React.useState(false)
  const st = ST[s.status] || ST.scheduled
  const isLive = s.status === 'live'
  const isCompleted = s.status === 'completed'

  const submit = async () => {
    if (!rating) return
    setBusy(true)
    try { await rateSession(s.id, rating, comment || undefined); onRated?.() }
    catch (e) { onErr?.(e?.response?.data?.error || e.message) }
    finally { setBusy(false) }
  }

  return (
    <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border-subtle)', background: isLive ? '#F0FBF7' : 'transparent', opacity: isCompleted && !s.canRate ? 0.75 : 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: st.color, animation: isLive ? 'pulse-ring 1.5s ease infinite' : 'none' }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 13 }}>{s.title}</div>
          <div className="row gap-3 muted mt-1" style={{ fontSize: 12, flexWrap: 'wrap' }}>
            <span>{s.trainer || '—'}{(s.mode === 'REMOTE' || s.mode === 'HYBRID') && <span style={{ marginLeft: 6, padding: '1px 6px', borderRadius: 999, background: tintBg('#378ADD'), color: tintFg('#378ADD'), fontSize: 10, fontWeight: 500 }}>{t.thRemote}</span>}</span>
            <span className="row gap-1"><Icon name="calendar" size={11} /> {fmtDate(s.scheduledAt, lang)}</span>
          </div>
        </div>
        <span style={{ padding: '3px 10px', borderRadius: 999, background: st.bg, color: st.color, fontSize: 11, fontWeight: 600, flexShrink: 0 }}>{statusLabel(s.status, t)}</span>
        {/* Action */}
        {(isLive || s.zoomJoinable) && s.conferenceUrl ? (
          <button onClick={() => gabungSesi(s).catch((e) => onErr?.(e?.response?.data?.error || e.message))} style={{ padding: '7px 16px', background: '#1D9E75', color: 'white', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            <Icon name="video" size={12} color="white" /> {t.thWatchLive}
          </button>
        ) : s.status === 'scheduled' ? (
          <div style={{ fontSize: 12, color: '#378ADD', fontWeight: 500, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 5 }}>
            <Icon name="lock" size={12} /> {t.thAvailableWhenLive}
          </div>
        ) : isCompleted && s.canRate ? (
          <Btn variant="secondary" size="sm" onClick={() => setOpen((o) => !o)}>{t.thRate}</Btn>
        ) : isCompleted && s.myRating ? (
          <Badge tone="success">{t.thRated}: {s.myRating}/5</Badge>
        ) : (
          <div style={{ fontSize: 12, color: 'var(--text-4)', flexShrink: 0 }}>{t.thSessionEnded}</div>
        )}
      </div>
      {open && isCompleted && s.canRate && (
        <div className="row gap-2 mt-2" style={{ alignItems: 'center', flexWrap: 'wrap', paddingLeft: 22 }}>
          <div className="row gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} onClick={() => setRating(n)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: n <= rating ? '#EE9B00' : 'var(--border-strong)' }}>★</button>
            ))}
          </div>
          <input value={comment} onChange={(e) => setComment(e.target.value)} maxLength={500} placeholder={t.thComment}
            style={{ flex: 1, minWidth: 160, height: 30, padding: '0 8px', border: '1px solid var(--border-strong)' }} />
          <Btn variant="primary" size="sm" disabled={busy || !rating} onClick={submit}>{t.thSubmit}</Btn>
        </div>
      )}
    </div>
  )
}
